import React from 'react';
import { Clock, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export const EditTaskDialog = ({ editTask, setEditTask, editDueDate, setEditDueDate, editHoursLog, setEditHoursLog, editStatus, setEditStatus, handleSaveTask }) => (
  <Dialog open={!!editTask} onOpenChange={() => setEditTask(null)}>
    <DialogContent data-testid="edit-task-dialog">
      <DialogHeader><DialogTitle>Edit Task</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <div>
          <Label className="mb-2 block">Activity</Label>
          <p className="text-sm text-slate-700 font-medium">{editTask?.activity}</p>
        </div>
        <div>
          <Label htmlFor="edit-due-date" className="mb-2 block">Due Date</Label>
          <Input id="edit-due-date" type="date" value={editDueDate} onChange={(e) => setEditDueDate(e.target.value)} tabIndex={-1} data-testid="edit-due-date-input" />
        </div>
        <div>
          <Label htmlFor="edit-hours" className="mb-2 flex items-center gap-1.5">
            Hours Log
            <span className="relative group/tip">
              <Info className="w-3.5 h-3.5 text-blue-500 cursor-help" />
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 hidden group-hover/tip:block bg-slate-800 text-white text-xs rounded-md px-3 py-2 whitespace-nowrap z-[9999] shadow-lg pointer-events-none">
                Enter time as .25, .50, .75, or whole hours
              </span>
            </span>
          </Label>
          <Input id="edit-hours" type="number" step="0.25" min="0" value={editHoursLog} onChange={(e) => setEditHoursLog(e.target.value)} data-testid="edit-hours-input" />
        </div>
        <div>
          <Label htmlFor="edit-status" className="mb-2 block">Status</Label>
          <Select value={editStatus} onValueChange={setEditStatus}>
            <SelectTrigger data-testid="edit-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={handleSaveTask} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="save-task-button">Save Changes</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const StartDateDialog = ({ showStartDateDialog, setShowStartDateDialog, currentStartDate, planName, startDate, setStartDate, handleStartDateUpdate }) => (
  <Dialog open={showStartDateDialog} onOpenChange={setShowStartDateDialog}>
    <DialogContent data-testid="user-start-date-dialog">
      <DialogHeader><DialogTitle>{currentStartDate ? 'Edit Start Date' : 'Set Start Date'}</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
          <p className="text-sm text-blue-800">
            {currentStartDate ? `Current start date: ${new Date(currentStartDate).toLocaleDateString()}` : 'Choose your study plan start date'}
          </p>
          {planName && <p className="text-xs text-blue-600 mt-1">Plan: {planName}</p>}
          <p className="text-xs text-blue-600 mt-1">All task due dates for this plan will be recalculated</p>
        </div>
        <div>
          <Label htmlFor="user-start-date">Start Date</Label>
          <Input id="user-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="user-start-date-input" />
        </div>
        <Button onClick={handleStartDateUpdate} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-user-start-date">
          {currentStartDate ? 'Update Start Date' : 'Set Start Date'}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const DayTasksDialog = ({ showDayTasksDialog, setShowDayTasksDialog, selectedDayTasks, handleEditTask }) => (
  <Dialog open={showDayTasksDialog} onOpenChange={setShowDayTasksDialog}>
    <DialogContent className="max-w-2xl" data-testid="day-tasks-dialog">
      <DialogHeader>
        <DialogTitle>
          Tasks for {selectedDayTasks.length > 0 && selectedDayTasks[0].due_date
            ? new Date(selectedDayTasks[0].due_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
            : 'Selected Day'}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3 mt-4 max-h-96 overflow-y-auto">
        {selectedDayTasks.map((task) => (
          <div key={task.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors">
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-semibold text-slate-900">{task.activity}</h4>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${task.status === 'open' ? 'bg-blue-100 text-blue-800 border border-blue-200' : 'bg-slate-100 text-slate-800 border border-slate-200'}`}>
                {task.status}
              </span>
            </div>
            <p className="text-sm text-slate-600 mb-2">{task.content}</p>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-1"><Clock className="w-4 h-4" />{task.hours_log} hrs logged</div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setShowDayTasksDialog(false); handleEditTask(task); }}>Edit Task</Button>
            </div>
          </div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);

export const CreateTaskDialog = ({ showCreateTaskDialog, setShowCreateTaskDialog, newTaskForm, setNewTaskForm, handleCreateCustomTask, planInfos }) => {
  const plans = planInfos || [];
  return (
    <Dialog open={showCreateTaskDialog} onOpenChange={setShowCreateTaskDialog}>
      <DialogContent data-testid="create-custom-task-dialog">
        <DialogHeader><DialogTitle>Add Custom Task</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          {plans.length > 1 && (
            <div>
              <Label htmlFor="new-task-plan">Assign to Plan</Label>
              <Select value={newTaskForm.study_plan_template_id || ''} onValueChange={(val) => setNewTaskForm(prev => ({ ...prev, study_plan_template_id: val }))}>
                <SelectTrigger data-testid="new-task-plan-select"><SelectValue placeholder="Select a plan" /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (<SelectItem key={p.plan_id} value={p.plan_id}>{p.plan_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label htmlFor="new-activity">Activity</Label>
            <Input id="new-activity" value={newTaskForm.activity} onChange={(e) => setNewTaskForm(prev => ({ ...prev, activity: e.target.value }))} placeholder="e.g., Review Chapter 3" data-testid="new-task-activity-input" />
          </div>
          <div>
            <Label htmlFor="new-content">Content</Label>
            <Textarea id="new-content" value={newTaskForm.content} onChange={(e) => setNewTaskForm(prev => ({ ...prev, content: e.target.value }))} placeholder="Task description" data-testid="new-task-content-input" />
          </div>
          <div>
            <Label htmlFor="new-goals">Goals</Label>
            <Textarea id="new-goals" value={newTaskForm.goals} onChange={(e) => setNewTaskForm(prev => ({ ...prev, goals: e.target.value }))} placeholder="What you want to achieve" data-testid="new-task-goals-input" />
          </div>
          <div>
            <Label htmlFor="new-due-date">Due Date</Label>
            <Input id="new-due-date" type="date" value={newTaskForm.due_date} onChange={(e) => setNewTaskForm(prev => ({ ...prev, due_date: e.target.value }))} data-testid="new-task-due-date-input" />
          </div>
          <Button onClick={handleCreateCustomTask} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-custom-task">Add Task</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SecurityQuestionDialog = ({ showSecuritySetup, setShowSecuritySetup, securityQuestions, selectedQuestion, setSelectedQuestion, securityAnswer, setSecurityAnswer, handleSetupSecurityQuestion }) => (
  <Dialog open={showSecuritySetup} onOpenChange={setShowSecuritySetup}>
    <DialogContent data-testid="security-question-dialog">
      <DialogHeader><DialogTitle>Set Up Security Question</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <p className="text-sm text-slate-600">Choose a security question and answer. This will be used to verify your identity when resetting your password.</p>
        <div>
          <Label htmlFor="security-question">Security Question</Label>
          <Select value={selectedQuestion} onValueChange={setSelectedQuestion}>
            <SelectTrigger data-testid="security-question-select"><SelectValue placeholder="Select a question" /></SelectTrigger>
            <SelectContent>
              {securityQuestions.map((q) => (<SelectItem key={q} value={q}>{q}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="security-answer">Your Answer</Label>
          <Input id="security-answer" value={securityAnswer} onChange={(e) => setSecurityAnswer(e.target.value)} placeholder="Enter your answer" data-testid="security-answer-input" />
          <p className="text-xs text-slate-500 mt-1">Answers are case-insensitive.</p>
        </div>
        <Button onClick={handleSetupSecurityQuestion} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-security-question">Save Security Question</Button>
      </div>
    </DialogContent>
  </Dialog>
);
